import React from 'react';
import PropTypes from 'prop-types';
import { getUserIcon } from 'components/utils/icon';
import Head from 'next/head';
import { useIsCustomDomain, useCustomDomain } from 'contexts/profile';
import JsonSchema from 'components/seo/JsonSchema';
import { get } from 'ui/lodash';

function ProductSeoSchema({
  shop,
  product,
}) {
  const isCustomDomain = useIsCustomDomain();
  const domain = useCustomDomain();
  const { uiConfig: { seo } = {} } = shop;

  const url = isCustomDomain ? `https://${domain}/${product.slug}` :
    `https://mywindo.shop/${product.shop.slug}/${product.slug}`
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    description: product.description,
    name: product.title,
    sku: product._id,
    identifier: product._id,
    offers: {
      '@type': 'Offer',
      'availability': 'https://schema.org/InStock',
      'price': product.discountedAmount.toFixed(2),
      'priceCurrency': shop.currency,
      url,
    },
    url,
    countryOfOrigin: shop.country === 'india' ? 'IN' : shop.country,
  };

  if (product?.colors?.length) {
    productSchema.color = product.colors.join();
  }

  if (product.images?.length) {
    productSchema.image = product.images[0].url;
  }

  const findAvailability = v => {
    if (v.availableType === 'infinite') {
      return 'https://schema.org/InStock';
    }
    if (v.available > 0) {
      return 'https://schema.org/InStock';
    }
    return 'https://schema.org/OutOfStock';
  };

  productSchema.model = product.variants.map(v => ({
    '@context': 'https://schema.org',
    '@type': 'ProductModel',
    description: product.description,
    sku: v._id,
    name: product.title,
    image: v.images?.[0]?.url ?? product?.images?.[0]?.url,
    url,
    offers: {
      '@type': 'Offer',
      url,
      countryOfOrigin: shop.country === 'india' ? 'IN' : shop.country,
      'availability': findAvailability(v),
      'price': v.discountedAmount.toFixed(2),
      'priceCurrency': shop.currency
    }
  }));

  if ((+product?.rating?.total) > 0) {
    productSchema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": product.rating.total,
    }
  }
  const color = get(shop, 'config.themeColor', '#F5603F');
  const isSubscribed = shop.plan !== 'free';

  const showManifest = isSubscribed && isCustomDomain;

  return (
    <Head>
      <meta property="og:title" content={shop?.name} key="ogtitle" />
      <meta property="og:image" content={product?.images?.[0]?.url ?? getUserIcon(shop)} key="ogimage" />
      <link rel="shortcut icon" href={getUserIcon(shop)} />
      <title>{seo?.title || shop.name || shop.title}</title>
      <meta property="description" content={seo?.description || shop.description} key="description" />
      {seo?.keywords && <meta property="keywords" content={seo.keywords} key="keywords" />}
      <JsonSchema schema={productSchema} />
      <meta name="theme-color" content={color} />
      {showManifest && <link rel="manifest" href="/manifest.json"/>}
    </Head>
  );
}

ProductSeoSchema.propTypes = {
  shop: PropTypes.object.isRequired,
  product: PropTypes.object.isRequired
};

export default ProductSeoSchema;
